import { APP_ROLE_PARAM_KEY, Role, useGetUserRoles } from 'auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import SignUpContentBlock from '@/modules/auth/components/SignUpContentBlock.tsx';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { useRegisterSsoInvestorMutation } from '@/modules/auth/queries/user.tsx';
import { authPaths } from '@/modules/auth/routes.tsx';
import { api } from '@/modules/common/api/index.ts';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  APPLY_REGISTER_PRIVATE_ID,
  AUTH_TYPE_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import { useRegisterByPrivateIdQuery } from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { useIssuerSelfAssignInvestorRole } from '@/modules/issuer-dashboard/queries/subscription.ts';
import { Button, SelectButtonOption, SelectButtons, Spinner } from 'ui';

export enum AuthTypes {
  LogIn = 'log_in',
  SignUp = 'sign_up',
}

const authTypesOptions: SelectButtonOption<AuthTypes>[] = [
  { value: AuthTypes.LogIn, label: 'Yes' },
  { value: AuthTypes.SignUp, label: 'No' },
];

export const TEST_PRIVATE_ID = 'e2d78ac7-385d-40fb-9012-3df650bd5c53';

const InvitationPage = () => {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const userRoles = useGetUserRoles();
  // to prevent multiple requests sending
  const isCreateInvestorRequestSent = useRef(false);

  const userHasInvestorRole = userRoles.includes(Role.SR_INVESTOR);
  // const userHasIssuerRole = userRoles.includes(Role.SR_ISSUER);
  // const userHasIssuerAdminRole = userRoles.includes(Role.SR_ISSUER_ADMIN);
  const authTypeSearchParam = params.get(AUTH_TYPE_SEARCH_PARAM_KEY);
  const applyRegisterId = params.get(APPLY_REGISTER_ID) || '';
  const applyRegisterPrivateId = params.get(APPLY_REGISTER_PRIVATE_ID) || '';

  useEffect(() => {
    if (
      !!applyRegisterId &&
      TEST_PRIVATE_ID.length === applyRegisterId?.length &&
      (!applyRegisterPrivateId || applyRegisterPrivateId.length !== TEST_PRIVATE_ID.length)
    ) {
      params.set(APPLY_REGISTER_PRIVATE_ID, applyRegisterId);
      setParams(params, { replace: false });
    }
  }, [applyRegisterId, applyRegisterPrivateId]);

  const { mutateAsync: issuerSubscribeAsInvestor, isLoading: isIssuerSubscriptionLoading } =
    useIssuerSelfAssignInvestorRole();
  const { mutateAsync: registerSsoInvestor } = useRegisterSsoInvestorMutation();

  // Read flag once on mount — before first render — to safely block navigation effects
  const [isSsoFlow] = useState(() => localStorage.getItem('sso_invitation_signup') === 'true');
  const [ssoHandled, setSsoHandled] = useState(false);
  const [isSsoRegistering, setIsSsoRegistering] = useState(false);
  const [ssoNavTarget, setSsoNavTarget] = useState<{ pathname: string; search: string } | null>(
    null,
  );

  const isNeededToCreateInvestor =
    auth.isAuthenticated &&
    !userHasInvestorRole &&
    TEST_PRIVATE_ID.length === applyRegisterPrivateId?.length &&
    !isIssuerSubscriptionLoading &&
    !isSsoFlow; // SSO users are handled by the SSO effect exclusively

  const isNeededToSubscribe =
    auth.isAuthenticated &&
    userHasInvestorRole &&
    TEST_PRIVATE_ID.length === applyRegisterPrivateId?.length &&
    !isIssuerSubscriptionLoading &&
    !isSsoFlow; // SSO users navigate via SSO effect exclusively

  const {
    data: applyRegister,
    isLoading: isLoadingApplyRegister,
    isError: isErrorApplyRegister,
  } = useRegisterByPrivateIdQuery(
    applyRegisterPrivateId,
    TEST_PRIVATE_ID.length !== applyRegisterPrivateId?.length,
  );

  const isSsoProcessing = isSsoFlow && auth.isAuthenticated && (!ssoHandled || isSsoRegistering);

  // Handle Google SSO return from invitation page
  useEffect(() => {
    if (!isSsoFlow || !auth.isAuthenticated || ssoHandled || isLoadingApplyRegister) return;

    localStorage.removeItem('sso_invitation_signup');
    setSsoHandled(true);

    if (userHasInvestorRole) {
      auth.signinRedirect();
      return;
    }

    if (TEST_PRIVATE_ID.length !== applyRegisterPrivateId?.length) return;

    const pollUntilUserCreated = async (attempts = 10, delay = 1500): Promise<void> => {
      for (let i = 0; i < attempts; i++) {
        try {
          await api.v1.getCurrentUserV1UsersCurrentGet();
          return;
        } catch (error: any) {
          if (error?.response?.status !== 404 || i === attempts - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    };

    setIsSsoRegistering(true);
    registerSsoInvestor(applyRegisterPrivateId)
      .then(() => auth.signinSilent())
      .then(() => pollUntilUserCreated(15, 2000))
      .then(() => {
        setSsoNavTarget({
          pathname: applyRegister?.data
            ? entityPaths.static.abnLookupEntity
            : entityPaths.static.entityList,
          search: createSearchParams({
            [APP_ROLE_PARAM_KEY]: Role.SR_INVESTOR,
            [APPLY_REGISTER_ID]: applyRegister?.data?.id?.toString() || '0',
            [APPLY_REGISTER_PRIVATE_ID]: applyRegisterPrivateId,
            [APPLY_REGISTER_CLASS_ID]: params.get(APPLY_REGISTER_CLASS_ID) || '',
          }).toString(),
        });
      })
      .catch(() => setIsSsoRegistering(false));
  }, [
    auth.isAuthenticated,
    isSsoFlow,
    ssoHandled,
    userHasInvestorRole,
    isLoadingApplyRegister,
    applyRegisterPrivateId,
  ]);

  useEffect(() => {
    if (!ssoNavTarget) return;
    navigate(ssoNavTarget, { replace: false });
  }, [ssoNavTarget, navigate]);

  const activeAuthTypeOption =
    authTypesOptions.find(({ value }) => value === authTypeSearchParam) || authTypesOptions[0];
  const isLoading = isLoadingApplyRegister || isIssuerSubscriptionLoading || isSsoProcessing;

  const handleChangeAuthType = useCallback(
    (type: AuthTypes) => {
      params.set(AUTH_TYPE_SEARCH_PARAM_KEY, type);
      setParams(params, { replace: false });
    },
    [params, setParams],
  );

  const title = useMemo(() => {
    if (applyRegister?.data) {
      return (
        <span>
          You were invited to invest to
          <span className="text-blue-700">
            {' '}
            {applyRegister?.data?.name?.replace(/(fund|company)/i, '')}{' '}
          </span>{' '}
          {applyRegister?.data?.type}.
        </span>
      );
    }

    return activeAuthTypeOption.value === AuthTypes.LogIn ? 'Sign in' : 'Get Started';
  }, [activeAuthTypeOption.value, applyRegister?.data]);

  const [signInUrl, signUpUrl] = useMemo(() => {
    const redirectParams = new URLSearchParams();
    const applyRegisterPrivateId = params.get(APPLY_REGISTER_PRIVATE_ID);
    const applyRegisterClassID = params.get(APPLY_REGISTER_CLASS_ID);

    redirectParams.set(APP_ROLE_PARAM_KEY, Role.SR_INVESTOR);

    if (applyRegister?.data)
      redirectParams.set(APPLY_REGISTER_ID, String(applyRegister?.data?.id || '0'));
    if (applyRegisterPrivateId)
      redirectParams.set(APPLY_REGISTER_PRIVATE_ID, applyRegisterPrivateId);
    if (applyRegisterClassID) redirectParams.set(APPLY_REGISTER_CLASS_ID, applyRegisterClassID);

    const keycloakRedirect = import.meta.env.VITE_KEYCLOAK_REDIRECT_URI;
    const signInUrl = `${keycloakRedirect}${
      authPaths.static.invitation
    }?${redirectParams.toString()}`;
    const redirectUrl = `${keycloakRedirect}?${redirectParams.toString()}`;
    const signUpUrl = `${keycloakRedirect}${
      applyRegister?.data ? entityPaths.static.abnLookupEntity : entityPaths.static.entityList
    }?${redirectParams.toString()}`;

    return [signInUrl, signUpUrl, redirectUrl];
  }, [applyRegister?.data, params]);

  // useEffect(() => {
  //   if (isLoadingApplyRegister) return;
  //
  //   if (isErrorApplyRegister) {
  //     auth.signinRedirect({ redirect_uri: redirectUrl });
  //   }
  // }, [auth, isErrorApplyRegister, redirectUrl, isLoadingApplyRegister]);

  // useEffect(() => {
  //   const userHasCurrentRole = userRoles.includes(rolesManipulator.current_user_role as Role);
  //
  //   if (userHasCurrentRole) return;
  //
  //   if (userHasIssuerAdminRole) {
  //     params.set(APP_ROLE_PARAM_KEY, Role.SR_ISSUER_ADMIN);
  //     setParams(params, { replace: false });
  //   } else if (userHasIssuerRole) {
  //     params.set(APP_ROLE_PARAM_KEY, Role.SR_ISSUER);
  //     setParams(params, { replace: false });
  //   }
  // }, [params, setParams, userHasIssuerAdminRole, userHasIssuerRole, userRoles]);

  useEffect(() => {
    if (isLoadingApplyRegister) return;

    const searchParams = createSearchParams({
      [APP_ROLE_PARAM_KEY]: Role.SR_INVESTOR,
      [APPLY_REGISTER_ID]: applyRegister?.data?.id?.toString() || '0',
      [APPLY_REGISTER_PRIVATE_ID]: params.get(APPLY_REGISTER_PRIVATE_ID)?.toString() || '',
      [APPLY_REGISTER_CLASS_ID]: params.get(APPLY_REGISTER_CLASS_ID)?.toString() || '',
    }).toString();

    if (isNeededToSubscribe) {
      isCreateInvestorRequestSent.current = true;
      navigate(
        {
          pathname: entityPaths.static.entityList,
          search: searchParams,
        },
        {
          replace: false,
        },
      );
    }

    if (isNeededToCreateInvestor && !isCreateInvestorRequestSent.current) {
      isCreateInvestorRequestSent.current = true;
      issuerSubscribeAsInvestor(+(applyRegister?.data?.id || '0'))
        .then(() => auth.signinSilent())
        .then(() => {
          navigate(
            {
              pathname: entityPaths.static.entityList,
              search: searchParams,
            },
            {
              replace: false,
            },
          );
        });
    }
  }, [
    auth,
    params,
    navigate,
    applyRegister?.data?.id,
    isNeededToSubscribe,
    issuerSubscribeAsInvestor,
    isNeededToCreateInvestor,
    isLoadingApplyRegister,
  ]);

  if (
    (TEST_PRIVATE_ID.length !== applyRegisterPrivateId?.length && !!applyRegisterPrivateId) ||
    isErrorApplyRegister
  ) {
    return <Navigate to={{ pathname: commonPaths.static.notFound, search: params.toString() }} />;
  }

  return (
    <SignUpPagesWrapper className="sm:justify-center justify-start">
      <div className="max-w-[660px] w-full px-6 flex flex-col gap-5 md:gap-10 justify-center">
        <span className="text-3xl sm:text-5xl font-semibold font-['RecifeDisplay']">{title}</span>
        {!isLoading && (
          <SelectButtons
            label="Do you have an account"
            options={authTypesOptions}
            selected={activeAuthTypeOption}
            onChange={handleChangeAuthType}
          />
        )}
        {isLoading && (
          <div className="flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && activeAuthTypeOption.value === AuthTypes.LogIn && (
          <Button onClick={() => auth.signinRedirect({ redirect_uri: signInUrl })}>
            Go to log in
          </Button>
        )}
        {!isLoading && activeAuthTypeOption.value === AuthTypes.SignUp && (
          <SignUpContentBlock redirect_url={signUpUrl} ssoRedirectUrl={signInUrl} />
        )}
      </div>
    </SignUpPagesWrapper>
  );
};

export default InvitationPage;
